import { trackEvent, type UmamiEventData } from "@giof/react-umami"

/**
 * Funnel events for the contact/lead path.
 *
 * Umami is privacy-focused and the site documents that publicly, so event
 * payloads must never carry submitted field values (name, email, message) or
 * anything that identifies a visitor. Keep properties to counts, flags and
 * short enum-like strings.
 */
export const ANALYTICS_EVENTS = {
  ctaClick: "cta_click",
  contactFormView: "contact_form_view",
  contactFormStart: "contact_form_start",
  contactFormSubmitSuccess: "contact_form_submit_success",
  contactFormSubmitError: "contact_form_submit_error",
} as const

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]

/**
 * Coarse bucket for a failed contact submission. Derived from the HTTP status
 * rather than the error message, so no server-authored text reaches analytics.
 */
export type ContactSubmitErrorKind = "validation" | "server" | "network" | "unknown"

export function contactSubmitErrorKind(status: number): ContactSubmitErrorKind {
  if (status === 400) return "validation"
  if (status >= 500) return "server"
  return "unknown"
}

/**
 * Track a funnel event. Safe to call anywhere: the underlying tracker no-ops
 * outside the browser and before the Umami script has loaded.
 */
export function track(event: AnalyticsEvent, data?: UmamiEventData): void {
  trackEvent(event, data)
}
